// Flat company payload shared by the Customer and Business forms. Keys are
// the backend's field names (BusinessCreateSerializer and friends), so API
// field errors map straight onto inputs; the label/helper maps hold the
// `fields.*` i18n key each form resolves in its own namespace.

export const COMPANY_FIELDS = [
  'persona_full_name',
  'persona_legal_name',
  'persona_tax_id',
  'persona_registration_number',
  'persona_email',
  'persona_website',
  'persona_country',
];

export const ADDRESS_FIELDS = [
  'address_street',
  'address_number',
  'address_complement',
  'address_district',
  'address_city',
  'address_state',
  'address_postal_code',
  'address_country',
  'phone_number',
];

// Required by the backend whenever a new company persona (with its address
// and phone) is created.
export const REQUIRED_COMPANY_FIELDS = ['persona_full_name', 'persona_country'];
export const REQUIRED_ADDRESS_FIELDS = [
  'address_street',
  'address_city',
  'address_state',
  'address_country',
  'phone_number',
];

export const COMPANY_FIELD_LABEL_KEY = {
  persona_full_name: 'fullName',
  persona_legal_name: 'legalName',
  persona_tax_id: 'taxId',
  persona_registration_number: 'registrationNumber',
  persona_email: 'email',
  persona_website: 'website',
  persona_country: 'country',
  address_street: 'street',
  address_number: 'number',
  address_complement: 'complement',
  address_district: 'district',
  address_city: 'city',
  address_state: 'state',
  address_postal_code: 'postalCode',
  address_country: 'addressCountry',
  phone_number: 'phone',
};

export const COMPANY_FIELD_HELPER_KEY = {
  persona_full_name: 'fullNameHelper',
  persona_tax_id: 'taxIdHelper',
  phone_number: 'phoneHelper',
};

// Tax IDs (CNPJ, including the new alphanumeric format) are stored as plain
// uppercase alphanumerics — same rule as the backend's normalize_tax_id.
export const normalizeTaxId = (value) => (value ?? '').replace(/[^0-9a-z]/gi, '').toUpperCase();

// Mirrors the Django model fields (Persona / Address / Phone): max_length and
// the field type (EmailField, URLField, ISO-2 country). `maxLength` is also
// set on the input, so these mostly catch legacy values and bad formats.
// EmailField/URLField defaults: 254 / 200 characters.
export const COMPANY_FIELD_RULES = {
  persona_full_name: { maxLength: 255 },
  persona_legal_name: { maxLength: 255 },
  persona_tax_id: { maxLength: 100 },
  persona_registration_number: { maxLength: 100 },
  persona_email: { maxLength: 254, format: 'email' },
  persona_website: { maxLength: 200, format: 'url' },
  persona_country: { maxLength: 2, format: 'country' },
  address_street: { maxLength: 255 },
  address_number: { maxLength: 20 },
  address_complement: { maxLength: 255 },
  address_district: { maxLength: 255 },
  address_city: { maxLength: 255 },
  address_state: { maxLength: 100 },
  address_postal_code: { maxLength: 20 },
  address_country: { maxLength: 2, format: 'country' },
  phone_number: { maxLength: 50 },
};

// Loose versions of Django's EmailValidator / URLValidator (the backend has
// the final say); URLField only takes http(s)/ftp(s) URLs with a host.
const FORMAT_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^(https?|ftps?):\/\/[^\s/?#]+\.[^\s/?#]+(\S*)$/i,
  country: /^[A-Z]{2}$/,
};

/**
 * First validation problem of a value against `rules` ({ maxLength, format })
 * and whether it is required, as `{ key, params }` for i18n (`validation.<key>`),
 * or null when valid. Blank optional values are always valid.
 */
export const validateFieldValue = (value, { required = false, maxLength, format } = {}) => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return required ? { key: 'required' } : null;
  if (maxLength && trimmed.length > maxLength) return { key: 'maxLength', params: { max: maxLength } };
  if (format && !FORMAT_PATTERNS[format].test(trimmed)) return { key: format };
  return null;
};

// ISO-3166-1 alpha-2: letters only, uppercased, at most 2.
export const normalizeCountry = (value) => (value ?? '').replace(/[^a-z]/gi, '').toUpperCase().slice(0, 2);
